import type { Image } from '~~/shared/globalTypes/product';

type ProcessedImage = {
  videoId?: string;
  filePath: string;
  name: string;
  video?: boolean;
  showJumboSwatch: boolean;
  swatchSpriteOffset: number;
  swatchSpriteUrlIndex: number;
  path?: MaybeRef<string>;
  isARIndicatorExp?: boolean;
  imageIndicatorEnabled?: boolean;
  vtoEligibleExperiment?: boolean;
  indicatorIndex?: number;
  marxentUrlForMobile?: string | null;
};

type ImageIndicatorSnapshot = {
  imageIndicatorEnabled: boolean;
  indicatorIndex: number | undefined;
  marxentUrlForMobile: string | null;
  vtoEligibleExperiment: boolean;
};

function createVideoSlide(videoId: string, firstImage: Image) {
  return {
    videoId,
    filePath: firstImage?.filePath,
    name: firstImage?.name || '',
    video: true,
    showJumboSwatch: firstImage?.showJumboSwatch,
    swatchSpriteOffset: firstImage?.swatchSpriteOffset || 0,
    swatchSpriteUrlIndex: firstImage?.swatchSpriteUrlIndex || 0,
  };
}

function insertVideoSlide(images: Image[], videoId: string): ProcessedImage[] {
  if (videoId && images[0]) {
    return [...images.slice(0, 3), createVideoSlide(videoId, images[0]), ...images.slice(3)];
  }

  return images;
}

function insertARIndicatorSlide(slides: ProcessedImage[], snapshot: ImageIndicatorSnapshot): ProcessedImage[] {
  if (!snapshot.imageIndicatorEnabled) {
    return slides;
  }

  const imageWithARIndicator = {
    ...slides[0],
    imageIndicatorEnabled: snapshot.imageIndicatorEnabled,
    indicatorIndex: snapshot.indicatorIndex,
    marxentUrlForMobile: snapshot.marxentUrlForMobile,
    vtoEligibleExperiment: snapshot.vtoEligibleExperiment,
  };

  return [...slides.slice(0, snapshot.indicatorIndex), imageWithARIndicator, ...slides.slice(snapshot.indicatorIndex)];
}

function applyUrlTemplate(slides: ProcessedImage[], urlTemplate: string): ProcessedImage[] {
  return slides.map((image) => ({
    ...image,
    path: image.filePath ? urlTemplate + image.filePath : '',
  }));
}

function buildPrimaryImage(slides: ProcessedImage[], urlTemplate: string): ProcessedImage {
  const firstImage = slides[0] || '';
  const path = slides[0]?.filePath ? urlTemplate + slides[0].filePath : '';

  return {
    ...firstImage,
    path,
  };
}

function buildSlideList(images: Image[], videoId: string, snapshot: ImageIndicatorSnapshot): ProcessedImage[] {
  return insertARIndicatorSlide(insertVideoSlide(images, videoId), snapshot);
}

function getVideoIndex(slides: ProcessedImage[]) {
  return slides.findIndex((image) => image.video);
}

export default function buildCarouselOutputs(images: Image[], videoId: string, snapshot: ImageIndicatorSnapshot, urlTemplate: string) {
  const slides = buildSlideList(images, videoId, snapshot);

  return {
    carouselImages: applyUrlTemplate(slides, urlTemplate),
    primaryImageData: buildPrimaryImage(slides, urlTemplate),
    videoIndex: getVideoIndex(slides),
  };
}
